import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Row,
  Col,
  CardTitle,
  CardText,
  Badge,
  Alert,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
  ListGroup,
  ListGroupItem,
  Spinner,
  Table,
  Button,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import classnames from "classnames";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import ItemServices from "../../../services/DibyaServices/ItemServices/ItemServices";
import restro from "../../../assets/orderingSystem/restrologo.png";
import { formatDateTimes } from "../../../Components/Common/FormatDate";
import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import { Triangle } from "react-loader-spinner";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";

const ItemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [activeSideTab, setActiveSideTab] = useState("0");
  const [activeAddonTab, setActiveAddonTab] = useState("addOns");
  const [allData, setAllData] = useState([]);
  const [summary, setSummary] = useState(null);

  // Pagination states for transaction logs
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const today = new Date();
  const todayFormatted = formatLocalDate(today);

  const lastMonthDate = new Date();
  lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
  const lastMonthFormatted = formatLocalDate(lastMonthDate);

  const [filters, setFilters] = useState({
    startDate: lastMonthFormatted,
    endDate: todayFormatted,
  });

  const formatDate = (date) => date?.toLocaleDateString("en-CA");

  const formatDateTimes = (date) =>
    date ? new Date(date).toLocaleString("en-US") : "N/A";

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const toggleSideTab = (tab) => {
    if (activeSideTab !== tab) setActiveSideTab(tab);
  };

  const toggleAddonTab = (tab) => {
    if (activeAddonTab !== tab) setActiveAddonTab(tab);
  };

  useEffect(() => {
    const fetchItemDetail = async () => {
      setLoading(true);
      try {
        const response = await ItemServices.productDetail(id);
        setProduct(response);
        setError(null);
      } catch (err) {
        console.error("Error fetching product details:", err);
        setError("Failed to load product details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchItemDetail();
    } else {
      setError("Invalid product ID.");
      setLoading(false);
    }
  }, [id]);

  const fetchData = async (
    page = currentPage,
    size = pageSize,
    from = filters.startDate,
    to = filters.endDate
  ) => {
    if (!from || !to) {
      setError("Please select both From and To dates");
      return;
    }

    setLoading(true);

    try {
      const response = await SalesReportService.getTransactionList({
        productId: id,
        fromDate: from,
        toDate: to,
        page,
        pageSize: size,
      });

      // Extract items array from the response object
      setAllData(response?.items || []);
      setTotalRows(response?.totalCount ?? response?.items?.length ?? 0);
      setSummary(response?.summary || null);
      setError(null);
    } catch (error) {
      console.error("Error fetching transaction data:", error);
      setError("Failed to load transaction data. Please try again.");
      setAllData([]);
      setTotalRows(0);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, pageSize);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
    fetchData(page, newPerPage);
  }; 

  useEffect(() => {
    fetchData(1, pageSize);
  }, []);

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      sortable: true,
      width: "80px",
      center: true,
    },
    {
      name: "Transaction Date",
      selector: (row) => formatDateTimes(row?.transactionDate),
      sortable: true,
    },
    {
      name: "Quantity",
      selector: (row) => row.quantity,
      sortable: true,
      right: true,
    },
    {
      name: "Unit Price",
      selector: (row) => row.unitPrice,
      cell: (row) => <NumberWithCommas number={row.unitPrice} />,
      sortable: true,
      right: true,
    },
    {
      name: "Debit",
      selector: (row) => `${row.debit}`,
      cell: (row) => <NumberWithCommas number={row.debit} />,
      sortable: true,
      right: true,
    },
    {
      name: "Credit",
      selector: (row) => `${row.credit}`,
      cell: (row) => <NumberWithCommas number={row.credit} />,
      sortable: true,
      right: true,
    },
    {
      name: "Total",
      selector: (row) => `${row.totalAmount}`,
      cell: (row) => <NumberWithCommas number={row.totalAmount} />,
      sortable: true,
      right: true,
    },
  ];

  if (loading && !product) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="text-center my-5">
            <Triangle
              visible={true}
              height="80"
              width="80"
              color="#5B71B9"
              ariaLabel="triangle-loading"
            />
            <p className="mt-3 text-muted">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="danger" className="my-4">
            <h5 className="alert-heading">Error!</h5>
            <p className="mb-0">{error}</p>
          </Alert>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="warning" className="my-4">
            <h5 className="alert-heading">Product Not Found</h5>
            <p className="mb-0">
              The requested product could not be found or may have been removed.
            </p>
          </Alert>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { title: "Back To List", onClick: () => navigate(-1) },
  ];

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb
          pageTitle="Product"
          title="Product Detail"
          breadcrumbItems={breadcrumbItems}
        />

        {/* Product Information Section */}
        <Row className="mb-4">
          {/* Product Image - 4 columns */}
          <Col lg={4}>
            <Card className="h-100">
              <CardBody className="p-4">
                <div className="text-center mb-3">
                  {product.displayImage && product.displayImage.length > 0 ? (
                    <img
                      src={product?.displayImage?.getCDNUrl()}
                      alt={product.name}
                      className="img-fluid rounded"
                      style={{
                        maxHeight: "320px",
                        objectFit: "contain",
                        width: "100%",
                      }}
                      onError={(e) => {
                        if (!e.target.dataset.error) {
                          e.target.dataset.error = "true";
                          e.target.src = restro;
                        }
                      }}
                    />
                  ) : (
                    <img
                      src={restro}
                      alt="Default Product"
                      className="img-fluid rounded"
                      style={{
                        maxHeight: "320px",
                        objectFit: "contain",
                        width: "100%",
                      }}
                    />
                  )}
                </div>

                {/* Thumbnail Images */}
                {product.productImages && product.productImages.length > 1 && (
                  <Row className="g-2">
                    {product.productImages.slice(0, 4).map((image, index) => (
                      <Col xs={3} key={image.id || index}>
                        <img
                          src={image?.cloudR2ImagePath?.getCDNUrl()}
                          alt={`View ${index + 1}`}
                          className="img-thumbnail w-100"
                          style={{
                            height: "60px",
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                          onError={(e) => {
                            if (!e.target.dataset.error) {
                              e.target.dataset.error = "true";
                              e.target.src = restro;
                            }
                          }}
                        />
                      </Col>
                    ))}
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Product Details - 8 columns */}
          <Col lg={8}>
            <Card className="h-100">
              <CardBody className="p-4">
                {/* Header */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <h3 className="text-primary mb-2">{product.name}</h3>
                    <Badge color="success" pill className="fs-6 px-3 py-2">
                      Rs. {product.price.toFixed(2)}
                    </Badge>
                  </div>
                  <p className="text-muted mb-0">
                    Category: {product.categoryName}
                  </p>
                </div>

                {/* Description */}
                <div className="mb-4">
                  <h5 className="mb-3">Description</h5>
                  <div className="bg-light p-3 rounded">
                    <p className="mb-0 text-muted">
                      {product.description || "No description available."}
                    </p>
                  </div>
                </div>

                {/* Tabs */}
                <div>
                  <Nav tabs>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => toggleTab("1")}
                        style={{ cursor: "pointer" }}
                      >
                        Details
                      </NavLink>
                    </NavItem>
                    {product.tags && (
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === "2" })}
                          onClick={() => toggleTab("2")}
                          style={{ cursor: "pointer" }}
                        >
                          Tags
                        </NavLink>
                      </NavItem>
                    )}
                    {product.attributes && product.attributes.length > 0 && (
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === "3" })}
                          onClick={() => toggleTab("3")}
                          style={{ cursor: "pointer" }}
                        >
                          Attributes
                        </NavLink>
                      </NavItem>
                    )}
                    {(product.addons?.addOns?.length > 0 ||
                      product.addons?.extras?.length > 0) && (
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === "4" })}
                          onClick={() => toggleTab("4")}
                          style={{ cursor: "pointer" }}
                        >
                          Add-ons
                        </NavLink>
                      </NavItem>
                    )}
                  </Nav>

                  <TabContent
                    activeTab={activeTab}
                    className="border border-top-0 p-3"
                  >
                    <TabPane tabId="1">
                      <ListGroup flush>
                        <ListGroupItem className="d-flex justify-content-between border-0 px-0">
                          <span className="text-muted">Name:</span>
                          <span className="fw-medium">{product.name}</span>
                        </ListGroupItem>
                        <ListGroupItem className="d-flex justify-content-between border-0 px-0">
                          <span className="text-muted">Category:</span>
                          <span className="fw-medium">
                            {product.categoryName}
                          </span>
                        </ListGroupItem>
                        {/* <ListGroupItem className="d-flex justify-content-between border-0 px-0">
                          <span className="text-muted">Price:</span>
                          <span className="fw-medium">
                            Rs. {product.price.toFixed(2)}
                          </span>
                        </ListGroupItem> */}
                      </ListGroup>
                    </TabPane>

                    <TabPane tabId="2">
                      {product.tags ? (
                        <div className="d-flex flex-wrap gap-2">
                          {product.tags.split(",").map((tag, index) => (
                            <Badge
                              key={index}
                              color="primary"
                              className="px-3 py-2"
                            >
                              {tag.trim()}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted mb-0">No tags available.</p>
                      )}
                    </TabPane>

                    <TabPane tabId="3">
                      {product.attributes && product.attributes.length > 0 ? (
                        <Table className="mb-0">
                          <thead>
                            <tr>
                              <th>Attribute</th>
                              <th className="text-center">Type</th>
                              <th className="text-end">Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {product.attributes.map((attr, index) => (
                              <tr key={index}>
                                <td>{attr.title}</td>
                                <td className="text-center">
                                  <Badge color="secondary">
                                    {attr.attributeType}
                                  </Badge>
                                </td>
                                <td className="text-end">Rs. {attr.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="text-muted mb-0">
                          No attributes available.
                        </p>
                      )}
                    </TabPane>

                    <TabPane tabId="4">
                      <Nav tabs className="mb-3">
                        {product.addons?.addOns?.length > 0 && (
                          <NavItem>
                            <NavLink
                              className={classnames({
                                active: activeAddonTab === "addOns",
                              })}
                              onClick={() => toggleAddonTab("addOns")}
                              style={{ cursor: "pointer" }}
                            >
                              Add-ons
                            </NavLink>
                          </NavItem>
                        )}
                        {product.addons?.extras?.length > 0 && (
                          <NavItem>
                            <NavLink
                              className={classnames({
                                active: activeAddonTab === "extras",
                              })}
                              onClick={() => toggleAddonTab("extras")}
                              style={{ cursor: "pointer" }}
                            >
                              Extras
                            </NavLink>
                          </NavItem>
                        )}
                      </Nav>

                      <TabContent activeTab={activeAddonTab}>
                        <TabPane tabId="addOns">
                          {product.addons?.addOns?.length > 0 ? (
                            <Table className="mb-0">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th className="text-end">Price</th>
                                </tr>
                              </thead>
                              <tbody>
                                {product.addons.addOns.map((addon, index) => (
                                  <tr key={index}>
                                    <td>{addon.name}</td>
                                    <td className="text-end">
                                      Rs. {addon.price.toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <p className="text-muted mb-0">
                              No add-ons available.
                            </p>
                          )}
                        </TabPane>

                        <TabPane tabId="extras">
                          {product.addons?.extras?.length > 0 ? (
                            <Table className="mb-0">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th className="text-end">Price</th>
                                </tr>
                              </thead>
                              <tbody>
                                {product.addons.extras.map((extra, index) => (
                                  <tr key={index}>
                                    <td>{extra.name}</td>
                                    <td className="text-end">
                                      Rs. {extra.price.toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <p className="text-muted mb-0">
                              No extras available.
                            </p>
                          )}
                        </TabPane>
                      </TabContent>
                    </TabPane>
                  </TabContent>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Child Products */}
        {product.childProductDtos && product.childProductDtos.length > 0 && (
          <Card className="mb-4">
            <CardBody className="p-4">
              <h5 className="mb-4">Included Products</h5>
              <Row className="g-3">
                {product.childProductDtos.map((item, index) => (
                  <Col md={3} sm={6} key={item.id || index}>
                    <Card className="h-100">
                      <img
                        src={item?.childProduct?.productImages[0]?.cloudR2ImagePath?.getCDNUrl()}
                        alt={item.childProduct.name}
                        className="card-img-top"
                        style={{ height: "180px", objectFit: "cover" }}
                        onError={(e) => {
                          if (!e.target.dataset.error) {
                            e.target.dataset.error = "true";
                            e.target.src = restro;
                          }
                        }}
                      />
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0">{item.childProduct.name}</h6>
                          <Badge color="success">
                            Rs. {item.price.toFixed(2)}
                          </Badge>
                        </div>
                        <p className="text-muted small mb-2">
                          {item.childProduct.category}
                        </p>
                        <p className="small mb-0">
                          {item.childProduct.description}
                        </p>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            </CardBody>
          </Card>
        )}

        {/* Side Details */}
        {product.sideDetails && product.sideDetails.length > 0 && (
          <Card className="mb-4">
            <CardBody className="p-4">
              <h5 className="mb-4">Side Items</h5>
              <Nav tabs>
                {product.sideDetails.map((side, index) => (
                  <NavItem key={index}>
                    <NavLink
                      className={classnames({
                        active: activeSideTab === index.toString(),
                      })}
                      onClick={() => toggleSideTab(index.toString())}
                      style={{ cursor: "pointer" }}
                    >
                      {side.sideName}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent
                activeTab={activeSideTab}
                className="border border-top-0 p-3"
              >
                {product.sideDetails.map((side, index) => (
                  <TabPane key={index} tabId={index.toString()}>
                    <div className="mb-3">
                      <Badge color="primary" className="px-3 py-2">
                        {side.noOfSidesChoosable} Choices Available
                      </Badge>
                    </div>
                    <Table responsive>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Description</th>
                          <th className="text-end">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {side.sideItems.map((item, itemIndex) => (
                          <tr key={itemIndex}>
                            <td>
                              <div className="d-flex align-items-center">
                                {item.productDetail.productImages?.[0] && (
                                  <img
                                    src={item?.productDetail?.productImages[0]?.cloudR2ImagePath?.getCDNUrl()}
                                    alt={item.productDetail.name}
                                    className="rounded me-2"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                      if (!e.target.dataset.error) {
                                        e.target.dataset.error = "true";
                                        e.target.src = restro;
                                      }
                                    }}
                                  />
                                )}
                                <span>{item.productDetail.name}</span>
                              </div>
                            </td>
                            <td>{item.productDetail.description || "N/A"}</td>
                            <td className="text-end">
                              Rs. {item.price.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TabPane>
                ))}
              </TabContent>
            </CardBody>
          </Card>
        )}

        {/* Transaction Logs Section */}
        <Card>
          <CardBody className="p-4">
            <Row className="mb-4 align-items-center">
              <Col md={8}>
                <h5 className="mb-0">Transaction Logs</h5>
                {filters.startDate === filters.endDate ? (
                  <p className="text-muted small mb-0">
                    Showing {filters.startDate}
                  </p>
                ) : (
                  <p className="text-muted small mb-0">
                    Showing {filters.startDate} to {filters.endDate}
                  </p>
                )}
              </Col>
              <Col md={4}>
                <div className="d-flex gap-2">
                  <InputGroup>
                    <Flatpickr
                      value={`${filters.startDate} to ${filters.endDate}`}
                      options={{
                        mode: "range",
                        dateFormat: "Y-m-d",
                        defaultDate: [filters.startDate, filters.endDate],
                      }}
                      onChange={(dates) => {
                        if (dates.length === 2) {
                          const start = formatLocalDate(dates[0]);
                          const end = formatLocalDate(dates[1]);
                          setFilters((prev) => ({
                            ...prev,
                            startDate: start,
                            endDate: end,
                          }));
                        }
                      }}
                      placeholder="Select Date Range"
                      className="form-control"
                    />
                    <InputGroupText style={{ cursor: "pointer" }}>
                      <i className="bx bx-calendar"></i>
                    </InputGroupText>
                  </InputGroup>

                  <Button
                    color="primary"
                    onClick={() => {
                      setCurrentPage(1);
                      fetchData(1, pageSize);
                    }}
                  >
                    Generate
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Summary Section */}
            {summary && (
              <Row className="mb-3">
                <Col md={3}>
                  <div className="border rounded p-3 text-center">
                    <p className="text-muted mb-1 small">Total Transactions</p>
                    <h5 className="mb-0">{summary.totalTransactions}</h5>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="border rounded p-3 text-center">
                    <p className="text-muted mb-1 small">Total Debit</p>
                    <h5 className="mb-0 text-danger">
                      <NumberWithCommas number={summary.totalDebit} />
                    </h5>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="border rounded p-3 text-center">
                    <p className="text-muted mb-1 small">Total Credit</p>
                    <h5 className="mb-0 text-success">
                      <NumberWithCommas number={summary.totalCredit} />
                    </h5>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="border rounded p-3 text-center">
                    <p className="text-muted mb-1 small">Net Amount</p>
                    <h5 className="mb-0">
                      <NumberWithCommas number={summary.netAmount} />
                    </h5>
                  </div>
                </Col>
              </Row>
            )}

            {loading ? (
              <div className="text-center py-5">
                <Triangle
                  visible={true}
                  height="60"
                  width="60"
                  color="#5B71B9"
                  ariaLabel="triangle-loading"
                />
                <p className="mt-3 text-muted">Loading transactions...</p>
              </div>
            ) : (
              <CustomDataTable
                responsive
                striped
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={pageSize}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                fixedHeader
                persistTableHead
                progressComponent={
                  <div className="my-3">
                    <Triangle
                      visible={true}
                      height="80"
                      width="80"
                      color="#5B71B9"
                      ariaLabel="triangle-loading"
                    />
                    <h5 className="mt-1">Loading...</h5>
                  </div>
                }
                columns={columns}
                data={allData}
                noDataComponent={
                  <div className="text-center my-5 w-100">
                    <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                    <h5 className="fw-semibold text-secondary">
                      No data Available
                    </h5>
                  </div>
                }
              />
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ItemDetail;
